import type { CSSProperties, ReactNode } from "react";

/**
 * Shell widths and the AppShell layout primitive.
 *
 * Per ``docs/ui-architecture.md``: workspace pages should fill desktop
 * monitors (1920×1080) — Linear / Stripe-Dashboard spaciousness, not
 * Notion-style centered narrow columns. The three width buckets:
 *
 * - **wide** (1800px) — Components workflow step + future canvas pages.
 *   Splits 60/40 or 50/50 between browser + detail rail.
 * - **workspace** (1600px) — Projects list, project detail, Files tab.
 *   Full-tab content uses the whole frame.
 * - **reading** (960px) — Documentation pages (none yet).
 *
 * Marketing / single-card pages don't use AppShell — they manage their
 * own centering (HomePage, auth-callback). Those keep ``max-w-[440px]``
 * to ``max-w-[1080px]`` per the design system.
 */

export const SHELL_WIDTHS = {
  wide: 1800,
  workspace: 1600,
  reading: 960,
} as const;

export type ShellWidth = keyof typeof SHELL_WIDTHS;

interface AppShellProps {
  width?: ShellWidth;
  children: ReactNode;
  /** Set true for pages with their own top-of-page chrome (back-link, etc). */
  flush?: boolean;
}

/** Standard padded workspace frame. */
export function AppShell({
  width = "workspace",
  children,
  flush = false,
}: AppShellProps) {
  const style: CSSProperties = {
    width: "100%",
    maxWidth: SHELL_WIDTHS[width],
    margin: "0 auto",
    // 32px horizontal padding (px-8) per the design system.
    padding: flush ? "0 32px" : "32px",
  };
  return (
    <main data-testid={`app-shell-${width}`} style={style}>
      {children}
    </main>
  );
}
